import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { USER_ROLES } from '../../utils/auth';

// Department label + accent hue (OKLCH hue angle). Ported 1:1 from the
// approved Claude Design handoff.
const DEPARTMENT_CONFIG = {
	managers:            { label: 'Product Managers',    hue: 230 },
	designers:           { label: 'Designers',           hue: 300 },
	content_managers:    { label: 'Content Managers',    hue: 320 },
	developers_wp:       { label: 'WP Developers',       hue: 270 },
	developers_frontend: { label: 'Frontend Developers', hue: 170 },
	developers_shopify:  { label: 'Shopify Developers',  hue: 130 },
	qa:                  { label: 'QA',                  hue: 80 },
	support:             { label: 'Support',             hue: 0 },
	office:              { label: 'Office',              hue: 30 },
	other:               { label: 'Other',               hue: 60 },
};

// Department display order — kept 1:1 with the approved Claude Design handoff.
const DEPARTMENT_ORDER = ['managers', 'designers', 'content_managers', 'developers_wp', 'developers_frontend', 'developers_shopify', 'support', 'qa', 'office', 'other'];

// Sub-team column order within a department; unknown teams sorted after,
// teamless members last.
const TEAM_ORDER = ['Product Devs', 'Alpha', 'Delta', 'Gamma', 'Sigma', 'Zeta', 'Kappa'];
const NO_TEAM_LABEL = 'No team';

// Department dropdown value meaning "show every department"
const ALL_DEPARTMENTS = 'all';

// Department pre-selected in the filter depending on the logged-in role
const ROLE_DEFAULT_DEPARTMENT = {
	[USER_ROLES.ADMIN]: ALL_DEPARTMENTS,
	[USER_ROLES.ADMIN_DESIGNER]: 'designers',
	[USER_ROLES.EDITOR]: ALL_DEPARTMENTS,
};

/* ---------- colour helpers (OKLCH from a department hue) ---------- */
const dotBg = (h) => `oklch(0.72 0.10 ${h})`;
const dotRing = (h) => `oklch(0.55 0.10 ${h} / 0.4)`;
const avatarBg = (h) => `oklch(0.95 0.04 ${h})`;
const avatarFg = (h) => `oklch(0.40 0.06 ${h})`;
const avatarRing = (h) => `oklch(0.85 0.05 ${h} / 0.5)`;
const pillSelBg = (h) => `oklch(0.94 0.06 ${h})`;
const pillSelBd = (h) => `oklch(0.65 0.10 ${h})`;
const checkBg = (h) => `oklch(0.55 0.10 ${h})`;
const chipBg = (h) => `oklch(0.96 0.03 ${h})`;
const chipBd = (h) => `oklch(0.78 0.06 ${h})`;

/* ---------- misc helpers ---------- */
const initials = (name) =>
	name.split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();

const fmtMoney = (n) => '$' + Math.round(n).toLocaleString('en-US');

/* ---------- atoms ---------- */
const Dot = ({ hue, size = 8 }) => (
	<span
		aria-hidden
		className='inline-block flex-shrink-0 rounded-full'
		style={{ width: size, height: size, background: dotBg(hue), boxShadow: `inset 0 0 0 1px ${dotRing(hue)}` }}
	/>
);

const Avatar = ({ name, hue }) => (
	<span
		aria-hidden
		className='inline-flex items-center justify-center rounded-full text-[10px] font-medium flex-shrink-0'
		style={{
			width: 22, height: 22,
			background: avatarBg(hue), color: avatarFg(hue),
			boxShadow: `inset 0 0 0 1px ${avatarRing(hue)}`,
		}}
	>{initials(name)}</span>
);

/* ---------- small auxiliary toggle ---------- */
const MiniToggle = ({ checked, onChange, label, disabled }) => (
	<label className={`inline-flex items-center gap-2 select-none text-[12.5px] text-gray-600 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
		<button
			type='button'
			role='switch'
			aria-checked={checked}
			disabled={disabled}
			onClick={() => !disabled && onChange()}
			className={`relative inline-flex h-[18px] w-[30px] rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
				checked ? 'bg-blue-600' : 'bg-gray-300'
			}`}
		>
			<span
				className='absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow transition-all'
				style={{ left: checked ? '14px' : '2px' }}
			/>
		</button>
		<span>{label}</span>
	</label>
);

/* ---------- department filter popover ---------- */
const DeptFilter = ({ value, onChange, options }) => {
	const [open, setOpen] = useState(false);
	const ref = useRef(null);

	useEffect(() => {
		const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
		document.addEventListener('mousedown', onDoc);
		return () => document.removeEventListener('mousedown', onDoc);
	}, []);

	const active = options.find(o => o.key === value) || options[0];

	return (
		<div ref={ref} className='relative'>
			<button
				type='button'
				onClick={() => setOpen(o => !o)}
				className='h-9 inline-flex items-center gap-2 pl-3 pr-2 rounded-lg bg-white border border-gray-300 text-[13px] text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400'
			>
				<span className='text-[10.5px] tracking-wider uppercase text-gray-400'>Dept</span>
				{active.hue !== null && <Dot hue={active.hue} />}
				<span className='font-medium'>{active.label}</span>
				<span className='text-[11px] tabular-nums px-1.5 py-[1px] rounded-md bg-gray-100 text-gray-500'>{active.count}</span>
				<svg viewBox='0 0 10 10' width='10' height='10' className='text-gray-400'>
					<path d='M2 4l3 3 3-3' fill='none' stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' strokeLinejoin='round' />
				</svg>
			</button>
			{open && (
				<div className='absolute z-30 mt-1 w-[260px] rounded-lg bg-white border border-gray-200 shadow-lg p-1'>
					{options.map(o => (
						<button
							key={o.key}
							type='button'
							onClick={() => { onChange(o.key); setOpen(false); }}
							className='w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left text-gray-800 hover:bg-gray-50'
						>
							{o.hue === null ? <span className='w-2 h-2' /> : <Dot hue={o.hue} />}
							<span className='flex-1 truncate'>{o.label}</span>
							<span className='text-[11px] tabular-nums text-gray-400'>{o.count}</span>
							{o.key === value && (
								<svg viewBox='0 0 10 10' width='11' height='11' className='text-blue-600'>
									<path d='M2 5.5l2 2L8 3' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
								</svg>
							)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

const UserSelector = ({
	users,
	selectedUsers = [],
	loading,
	isDataStillUpdating,
	onUserSelect,
	onClearUsers,
	onRemoveUser,
	separateView = true,
	onToggleView,
	showDetails = true,
	onToggleShowDetails,
	userRole = null,
	employeeMetrics = {},
}) => {
	const [departmentFilter, setDepartmentFilter] = useState(
		() => ROLE_DEFAULT_DEPARTMENT[userRole] || ALL_DEPARTMENTS
	);
	const [searchQuery, setSearchQuery] = useState('');
	const [hovered, setHovered] = useState(null);

	const isUserSelected = (gid) => selectedUsers.includes(gid);

	// Admin-only extended info for a user (department/salary metrics)
	const getEmployeeInfo = (userName) => {
		if (userRole !== USER_ROLES.ADMIN) return null;
		const data = employeeMetrics[userName];
		if (!data || !data.has_salary_data) return null;
		return data;
	};

	const getUserDepartment = (userName) => {
		const dept = employeeMetrics[userName]?.department;
		return dept && DEPARTMENT_CONFIG[dept] ? dept : 'other';
	};

	const getUserTeam = (userName) => employeeMetrics[userName]?.team || null;

	/* ---- bucket users into department → (sub-team) columns ---- */
	const usersByDept = {};
	users.forEach(u => {
		const dept = getUserDepartment(u.name);
		(usersByDept[dept] = usersByDept[dept] || []).push(u);
	});

	const buildColumns = () => {
		const columns = [];
		DEPARTMENT_ORDER.forEach(deptKey => {
			const deptUsers = usersByDept[deptKey];
			if (!deptUsers || deptUsers.length === 0) return;
			const config = DEPARTMENT_CONFIG[deptKey];
			const hasTeams = deptUsers.some(u => getUserTeam(u.name));

			if (!hasTeams) {
				columns.push({ groupId: deptKey, deptKey, config, subteam: null, users: deptUsers });
				return;
			}

			const byTeam = {};
			deptUsers.forEach(u => {
				const key = getUserTeam(u.name) || NO_TEAM_LABEL;
				(byTeam[key] = byTeam[key] || []).push(u);
			});
			const known = TEAM_ORDER.filter(t => byTeam[t]);
			const extra = Object.keys(byTeam)
				.filter(t => t !== NO_TEAM_LABEL && !TEAM_ORDER.includes(t))
				.sort();
			const order = [...known, ...extra];
			if (byTeam[NO_TEAM_LABEL]) order.push(NO_TEAM_LABEL);

			order.forEach(team => {
				columns.push({
					groupId: `${deptKey}__${team}`,
					deptKey, config, subteam: team, users: byTeam[team],
				});
			});
		});
		return columns;
	};

	const allColumns = buildColumns();

	/* ---- department dropdown options ---- */
	const deptOptions = [
		{ key: ALL_DEPARTMENTS, label: 'All departments', hue: null, count: users.length },
		...DEPARTMENT_ORDER
			.filter(d => usersByDept[d] && usersByDept[d].length > 0)
			.map(d => ({ key: d, label: DEPARTMENT_CONFIG[d].label, hue: DEPARTMENT_CONFIG[d].hue, count: usersByDept[d].length })),
	];

	/* ---- apply filters: department dropdown + name search ---- */
	const query = searchQuery.trim().toLowerCase();
	const visibleColumns = allColumns
		.filter(col => departmentFilter === ALL_DEPARTMENTS || col.deptKey === departmentFilter)
		.map(col => ({
			...col,
			users: query ? col.users.filter(u => u.name.toLowerCase().includes(query)) : col.users,
		}))
		.filter(col => col.users.length > 0);

	const visibleCount = visibleColumns.reduce((sum, c) => sum + c.users.length, 0);

	/* ---- selected users (for the chip rail) ---- */
	const selectedUserObjects = users.filter(u => selectedUsers.includes(u.gid));

	const handleClearUsers = () => {
		if (onClearUsers) onClearUsers();
		else selectedUsers.forEach(gid => onUserSelect(gid));
	};

	const handleRemoveUser = (gid) => {
		if (onRemoveUser) onRemoveUser(gid);
		else onUserSelect(gid);
	};

	const togglesDisabled = loading || isDataStillUpdating;
	const showSeparateToggle = selectedUsers.length > 1;

	/* ---- info card content ---- */
	/* ---- custom hover info card: position, show/hide ---- */
	const showInfoCard = (e, name, info) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const CARD_W = 252;
		const GAP = 12;
		// To the side of the pill: right if it fits the viewport, else left.
		const placeRight = rect.right + GAP + CARD_W <= window.innerWidth;
		setHovered({
			name,
			info,
			side: placeRight ? 'right' : 'left',
			centerY: rect.top + rect.height / 2,
			left: placeRight ? rect.right + GAP : rect.left - GAP - CARD_W,
		});
	};

	const hideInfoCard = () => setHovered(null);

	const renderInfoCard = (name, info) => {
		const deptLabel = DEPARTMENT_CONFIG[info.department]?.label || info.department;
		return (
			<div className='w-[252px] p-3.5 text-left'>
				<div className='text-[14px] font-semibold text-gray-900 truncate mb-2.5'>{name}</div>
				<div className='space-y-[7px]'>
					<InfoRow label='Base' value={fmtMoney(info.base_salary)} />
					<InfoRow label='Tax rate' value={`${info.tax_rate}%`} />
					<InfoRow label='Final' value={fmtMoney(info.final_salary)} tone='ink' bold />
					<div className='h-px bg-gray-200 my-2' />
					<InfoRow
						label={`${deptLabel} avg`}
						value={fmtMoney(info.department_avg)}
						tone={info.final_salary >= info.department_avg ? 'pos' : 'neg'}
					/>
				</div>
			</div>
		);
	};

	/* ---- person pill ---- */
	const renderPill = (u, hue) => {
		const selected = isUserSelected(u.gid);
		const info = getEmployeeInfo(u.name);
		const updating = selected && (loading || isDataStillUpdating);

		const pillStyle = selected
			? { background: pillSelBg(hue), boxShadow: `inset 0 0 0 1px ${pillSelBd(hue)}` }
			: { background: '#fff', boxShadow: 'inset 0 0 0 1px #e5e7eb' };

		return (
			<button
				key={u.gid}
				type='button'
				onClick={() => onUserSelect(u.gid)}
				onMouseEnter={info ? (e) => showInfoCard(e, u.name, info) : undefined}
				onMouseLeave={info ? hideInfoCard : undefined}
				disabled={loading && selected}
				aria-pressed={selected}
				className={`group relative flex items-center gap-2 w-full h-8 pl-1 pr-2.5 rounded-full text-[12.5px] font-medium text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
					updating ? 'animate-pulse' : ''
				} ${!selected ? 'hover:bg-gray-50' : ''}`}
				style={pillStyle}
			>
				<Avatar name={u.name} hue={hue} />
				<span className='truncate min-w-0 flex-1 text-left'>{u.name}</span>
				{selected && (
					<span
						aria-hidden
						className='absolute -top-1 -right-1 inline-flex items-center justify-center w-[14px] h-[14px] rounded-full text-white'
						style={{ background: checkBg(hue) }}
					>
						<svg viewBox='0 0 10 10' width='8' height='8'>
							<path d='M2 5.5l2 2L8 3' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
						</svg>
					</span>
				)}
			</button>
		);
	};

	/* ---- department / sub-team column ---- */
	const renderColumn = (col) => {
		const { config, subteam, users: colUsers } = col;
		const pickedHere = colUsers.filter(u => isUserSelected(u.gid)).length;
		const isProductDevs = subteam === 'Product Devs';

		return (
			<div
				key={col.groupId}
				className='flex-shrink-0 rounded-lg border border-gray-200 px-3 py-2.5'
				style={{ minWidth: isProductDevs ? 186 : 166, maxWidth: isProductDevs ? 214 : 194 }}
			>
				<header className='flex items-center justify-between gap-2 mb-2'>
					<div className='flex items-center gap-1.5 min-w-0'>
						<Dot hue={config.hue} />
						<span className='text-[12.5px] font-medium text-gray-800 truncate'>
							{subteam || config.label}
						</span>
						{subteam && (
							<span className='text-[10px] uppercase tracking-wider text-gray-400 truncate'>
								· {config.label.split(' ')[0]}
							</span>
						)}
					</div>
					<span className='text-[10.5px] tabular-nums text-gray-400 flex-shrink-0'>
						{pickedHere > 0 ? `${pickedHere}/${colUsers.length}` : colUsers.length}
					</span>
				</header>
				<div className='flex flex-col gap-1.5'>
					{colUsers.map(u => renderPill(u, config.hue))}
				</div>
			</div>
		);
	};

	const bodyLabel = departmentFilter === ALL_DEPARTMENTS
		? 'Departments'
		: (DEPARTMENT_CONFIG[departmentFilter]?.label || 'Departments');

	return (
		<div className='rounded-2xl border border-gray-200 bg-white'>
			{/* ============ TOOLBAR ============ */}
			<div className='flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200'>
				{/* search */}
				<div className='relative'>
					<svg viewBox='0 0 16 16' width='14' height='14' className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400'>
						<circle cx='7' cy='7' r='4.5' fill='none' stroke='currentColor' strokeWidth='1.3' />
						<path d='M10.5 10.5L14 14' stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' />
					</svg>
					<input
						type='text'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder='Search people…'
						className='h-9 w-[240px] rounded-lg border border-gray-300 pl-9 pr-7 text-[13px] text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400'
					/>
					{searchQuery && (
						<button
							type='button'
							onClick={() => setSearchQuery('')}
							aria-label='Clear search'
							className='absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-md text-gray-400 hover:text-gray-700'
						>×</button>
					)}
				</div>

				<div className='h-6 w-px bg-gray-200' />

				<DeptFilter value={departmentFilter} onChange={setDepartmentFilter} options={deptOptions} />

				<div className='h-6 w-px bg-gray-200' />

				<MiniToggle
					checked={showDetails}
					onChange={() => onToggleShowDetails && onToggleShowDetails()}
					label='Show details'
					disabled={togglesDisabled}
				/>
				{showSeparateToggle && (
					<MiniToggle
						checked={separateView}
						onChange={() => onToggleView && onToggleView()}
						label='Separate tables'
						disabled={togglesDisabled}
					/>
				)}

				<div className='ml-auto flex items-center gap-2'>
					<span className='text-[11px] uppercase tracking-wider text-gray-400'>Selected</span>
					<span className='text-[13px] font-semibold tabular-nums text-gray-800'>
						{selectedUsers.length}
						<span className='font-normal text-gray-400'> / {users.length}</span>
					</span>
				</div>
			</div>

			{/* ============ BODY: horizontal column rail ============ */}
			<section>
				<div className='px-4 pt-3 pb-2 flex items-center justify-between'>
					<div className='text-[10.5px] uppercase tracking-[0.14em] text-gray-400'>
						{bodyLabel}<span className='mx-2 text-gray-300'>·</span>scroll →
					</div>
					<div className='text-[10.5px] text-gray-400'>{visibleCount} people</div>
				</div>

				<div className='overflow-x-auto px-4 pb-4'>
					<div className='flex items-stretch gap-3 min-h-[180px]'>
						{loading && users.length === 0 ? (
							Array.from({ length: 8 }).map((_, i) => (
								<div key={i} className='flex-shrink-0 w-[180px] rounded-lg border border-gray-200 p-2.5'>
									<div className='h-3 w-20 rounded bg-gray-200 animate-pulse mb-2.5' />
									<div className='flex flex-col gap-1.5'>
										<div className='h-8 rounded-full bg-gray-100 animate-pulse' />
										<div className='h-8 rounded-full bg-gray-100 animate-pulse' />
										<div className='h-8 rounded-full bg-gray-100 animate-pulse' />
									</div>
								</div>
							))
						) : visibleColumns.length === 0 ? (
							<div className='flex items-center justify-center w-full py-10 text-[13px] text-gray-400'>
								{query ? `No people match “${searchQuery}”.` : 'No users to show.'}
							</div>
						) : (
							visibleColumns.map(renderColumn)
						)}
					</div>
				</div>
			</section>

			{/* ============ SELECTION RAIL — only shown when something is selected ============ */}
			{selectedUserObjects.length > 0 && (
				<div className='flex items-start gap-3 px-4 py-2.5 border-t border-gray-200 bg-gray-50'>
					<span className='text-[10.5px] uppercase tracking-[0.14em] text-gray-400 flex-shrink-0 pt-1.5'>
						Selection
					</span>
					<div className='flex-1 min-w-0 flex flex-wrap items-center gap-1.5'>
						{selectedUserObjects.map(u => {
							const hue = DEPARTMENT_CONFIG[getUserDepartment(u.name)].hue;
							const team = getUserTeam(u.name);
							const tag = team || DEPARTMENT_CONFIG[getUserDepartment(u.name)].label.split(' ')[0];
							return (
								<span
									key={u.gid}
									className='inline-flex items-center gap-1.5 h-7 pl-1 pr-1 rounded-full text-[12px] text-gray-800'
									style={{ background: chipBg(hue), boxShadow: `inset 0 0 0 1px ${chipBd(hue)}` }}
								>
									<Avatar name={u.name} hue={hue} />
									<span className='whitespace-nowrap'>{u.name}</span>
									<span className='text-[10px] uppercase tracking-wider text-gray-400 px-1'>{tag}</span>
									<button
										type='button'
										onClick={() => handleRemoveUser(u.gid)}
										disabled={loading}
										aria-label={`Remove ${u.name}`}
										className='w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-50'
									>
										<svg viewBox='0 0 10 10' width='9' height='9' aria-hidden>
											<path d='M2.5 2.5l5 5M7.5 2.5l-5 5' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
										</svg>
									</button>
								</span>
							);
						})}
					</div>
					<button
						type='button'
						onClick={handleClearUsers}
						disabled={loading}
						className='flex-shrink-0 text-[12px] font-medium px-2.5 h-7 rounded-md text-gray-600 bg-white border border-gray-200 hover:text-gray-900 disabled:opacity-50'
					>Clear all</button>
				</div>
			)}

			{/* Custom info card — portalled to <body> so position:fixed is true
			    viewport space; sits to the side of the pill with an arrow.
			    Instant show/hide (no fade, no trail). */}
			{hovered && createPortal(
				<div
					className='fixed z-[9999] pointer-events-none'
					style={{ top: hovered.centerY, left: hovered.left, transform: 'translateY(-50%)' }}
				>
					<div
						className='relative w-[252px] rounded-xl bg-white'
						style={{ boxShadow: '0 12px 32px rgba(0, 0, 0, 0.14), 0 0 0 1px #e5e7eb' }}
					>
						<div
							className='absolute top-1/2'
							style={{
								width: 0,
								height: 0,
								marginTop: -7,
								borderTop: '7px solid transparent',
								borderBottom: '7px solid transparent',
								...(hovered.side === 'right'
									? { left: -7, borderRight: '7px solid #fff' }
									: { right: -7, borderLeft: '7px solid #fff' }),
							}}
						/>
						{renderInfoCard(hovered.name, hovered.info)}
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

/* ---------- hover info card row ---------- */
const InfoRow = ({ label, value, tone, bold }) => {
	const color =
		tone === 'pos' ? 'oklch(0.52 0.14 145)' :
		tone === 'neg' ? 'oklch(0.56 0.16 25)' :
		tone === 'ink' ? '#1f2937' : '#6b7280';
	return (
		<div className='flex items-baseline justify-between gap-4'>
			<span className='text-[11px] uppercase tracking-wider text-gray-400'>{label}</span>
			<span className={`text-[12.5px] tabular-nums ${bold ? 'font-semibold' : ''}`} style={{ color }}>{value}</span>
		</div>
	);
};

InfoRow.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
	tone: PropTypes.oneOf(['pos', 'neg', 'ink']),
	bold: PropTypes.bool,
};

Dot.propTypes = { hue: PropTypes.number.isRequired, size: PropTypes.number };
Avatar.propTypes = { name: PropTypes.string.isRequired, hue: PropTypes.number.isRequired };
MiniToggle.propTypes = {
	checked: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
	label: PropTypes.string.isRequired,
	disabled: PropTypes.bool,
};
DeptFilter.propTypes = {
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	options: PropTypes.array.isRequired,
};

UserSelector.propTypes = {
	users: PropTypes.arrayOf(
		PropTypes.shape({
			gid: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
		})
	).isRequired,
	selectedUsers: PropTypes.arrayOf(PropTypes.string),
	loading: PropTypes.bool.isRequired,
	isDataStillUpdating: PropTypes.bool.isRequired,
	onUserSelect: PropTypes.func.isRequired,
	onClearUsers: PropTypes.func,
	onRemoveUser: PropTypes.func,
	separateView: PropTypes.bool,
	onToggleView: PropTypes.func,
	showDetails: PropTypes.bool,
	onToggleShowDetails: PropTypes.func,
	userRole: PropTypes.string,
	employeeMetrics: PropTypes.objectOf(
		PropTypes.shape({
			employee: PropTypes.string,
			department: PropTypes.string,
			team: PropTypes.string,
			base_salary: PropTypes.number,
			tax_rate: PropTypes.number,
			final_salary: PropTypes.number,
			department_avg: PropTypes.number,
			coefficient: PropTypes.number,
			adjustment_factor: PropTypes.number,
			has_salary_data: PropTypes.bool,
		})
	),
};

export default UserSelector;
